'use client';

import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BoardCover, boardInitials } from '@/features/lobby/ui/BoardCover';
import { useWallet } from '@/hooks/use-wallet';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import {
  useDepositProtectionStatus,
  useEnableDepositProtection,
  useProtectionHistory,
  parseAllowedUgmsList,
  type ProtectionHistoryItem,
} from '@/hooks/use-deposit-protection';
import { useOnchainRevoke } from '@/hooks/use-onchain-revoke';
import { useProtectableUgms } from '@/features/protection/use-protectable-ugms';
import { useProtectionWalletFunding } from '@/features/protection/use-wallet-funding';
import {
  buildBoardCoverage,
  boardCoverageReasonCopy,
  type BoardCoverageRow,
} from './board-coverage';
import type { CoinSeatsData } from '@/components/seats/coin-seats-card';
import { compactNum } from '@/lib/drawer-utils';
import { formatUnits, isAddress, type Address } from 'viem';
import { handleTxError, isUserRejectionError } from '@/components/drawers/sim-error';
import { toFriendlyAuthError, isWalletNotLinkedError } from './friendly-error';
import {
  ProtectionStatusPill,
  protectionUiState,
} from './protection-status-pill';
import { ProtectionExplainer } from './ProtectionExplainer';

// Match the onchain enforcer defaults (3-day gate → 7-day target).
const RUNWAY_GATE_SECONDS = 3 * 24 * 60 * 60;
const TARGET_RUNWAY_SECONDS = 7 * 24 * 60 * 60;

// ─── Protection-history row helpers ──────────────────────────────
// Self-contained (display-only) so the footer's scrollable history
// list doesn't have to reach into the dialog's formatter chain.

/** Deterministic per-board chip hue for the history rows. */
function historyBoardHue(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 70%, 55%)`;
}

/** Compact "+$1.20" / "+12.4M TKO" amount for one top-up row. */
function formatHistoryAmount(item: ProtectionHistoryItem): string {
  let principal: bigint;
  try {
    principal = BigInt(item.principal);
  } catch {
    return '—';
  }
  const usdcAddr = (process.env.NEXT_PUBLIC_USDC_ADDRESS ?? '').toLowerCase();
  if (usdcAddr && item.taxToken.toLowerCase() === usdcAddr) {
    const usd = Number(principal) / 1e6;
    return usd < 0.01 ? '<$0.01' : `$${usd.toFixed(2)}`;
  }
  return `${compactNum(Number(principal) / 1e18)} TKO`;
}

/** Compact relative time: "2m" / "3h" / "5d". */
function timeAgoShort(unixMs: number): string {
  const min = Math.floor((Date.now() - unixMs) / 60_000);
  if (min < 60) return `${Math.max(1, min)}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

/**
 * Deposit-Protection panel for the manage modal — the sole entry point
 * for the feature. Renders as a right-hand sidebar rail on desktop
 * (`lg:`) and stacks below the tile list on narrow screens, so the
 * (often short) tile list keeps the full-height left column instead of
 * being squeezed by a tall always-on footer.
 *
 *   - A glanceable weekly-cover card shows what's covered (active) or
 *     the projected cover (inactive).
 *   - A single Switch reflects live status. Turning it ON signs +
 *     enables inline (auto-sized caps from the holder's weekly burn);
 *     turning it OFF revokes on-chain and clears the server row (with a
 *     small inline confirm) — there's no separate "pause".
 *   - A tabbed "Protected Boards" / "Protection History" list shows what's
 *     actually covered; "How it works" opens a compact explainer dialog.
 *     There is no separate Manage dialog — this rail is the sole surface.
 *   - When active, an under-cover alert + "Increase cover" prompt shows
 *     if the signed cap has fallen below the current weekly tax.
 */

/** MetaMask fox mark. Land Protection currently supports MetaMask only,
 *  so the mark carries meaning — keep a real `alt`, not `aria-hidden`. */
function MetaMaskMark({ className }: { className?: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src="/wallets/metamask-fox.svg" alt="MetaMask" className={className} />;
}

export function ProtectionFooter({ groups }: { groups: CoinSeatsData[] }) {
  const wallet = useWallet();
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();
  const address: Address | null =
    wallet.address && isAddress(wallet.address) ? (wallet.address as Address) : null;

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } =
    useDepositProtectionStatus(address);
  const { enable, isPending: isEnabling, capability } = useEnableDepositProtection();
  // Turning the toggle OFF revokes on-chain (7710) / best-effort (7715)
  // AND clears the server row — there's no "pause" anymore.
  const { revokeOnchain, isPending: revokePending } = useOnchainRevoke();

  const [howOpen, setHowOpen] = useState(false);
  const [confirmingOff, setConfirmingOff] = useState(false);
  // Surfaces a failed enable inline instead of letting the toggle snap
  // silently back to OFF as if nothing happened. `needsSignIn` flips the
  // block from the red "Try again" recovery to an orange "Sign In" one
  // when the failure is just an unlinked wallet (sign-in fixes it; a
  // retry alone wouldn't).
  const [enableError, setEnableError] = useState<
    { message: string; needsSignIn: boolean } | null
  >(null);
  // Calm guidance after a turn-off that couldn't act on-chain (the
  // MetaMask 7715 case) — the row is cleared server-side regardless.
  const [offNotice, setOffNotice] = useState<string | null>(null);

  const state = protectionUiState(status, capability, statusLoading);
  const isActive = state === 'active';
  const signingPath = capability.result?.signingPath ?? '7710-direct';
  const showMetaMaskNote = signingPath === '7715-grant';

  // Size the budget + protectable set from the SAME shared hook the
  // dialog uses, so a MetaMask holder never grants for a V2.1 board the
  // server would reject (which used to leave an orphaned permission and
  // snap the toggle back OFF). `protectableUgmsList` is what we sign.
  const { budget, protectableUgmsList, excludedV21Tiles, ugmRows, v21UgmsLower } =
    useProtectableUgms({
      groups,
      signingPath,
      takeoverPriceUsd,
    });

  // Per-board coverage for the "Protected Boards" tab: which of the
  // holder's boards the signed delegation actually covers, and — for
  // those it doesn't — a one-line non-technical reason. `liveAllowed`
  // is the CURRENT env allowlist (literal `process.env.X` for the Next
  // inliner), so a board onboarded after the holder signed reads as
  // "re-enable to cover" rather than "not onboarded yet".
  const liveAllowedUgms = useMemo(() => {
    const s = new Set<string>();
    for (const a of parseAllowedUgmsList(process.env.NEXT_PUBLIC_PROTECTION_ALLOWED_UGMS)) {
      s.add(a.toLowerCase());
    }
    return s;
  }, []);
  const coverageRows = useMemo(
    () =>
      buildBoardCoverage({
        ugmRows,
        protectedUgms: status?.protectedUgms ?? [],
        liveAllowedUgms,
        v21UgmsLower,
        signingPath,
      }),
    [ugmRows, status?.protectedUgms, liveAllowedUgms, v21UgmsLower, signingPath],
  );

  // Recent auto-top-ups, shown as a scrollable list under the toggle
  // when protection is active. `boardByUgm` resolves each row's UGM to
  // a board name + chip colour.
  const { data: historyData } = useProtectionHistory(isActive ? address : null);
  const historyItems = historyData?.items ?? [];
  const boardByUgm = useMemo(() => {
    const m = new Map<string, { label: string; hue: string }>();
    for (const row of ugmRows) {
      const head = row.boards[0];
      if (!head) continue;
      m.set(row.ugm.toLowerCase(), { label: head.poolName, hue: historyBoardHue(head.poolId) });
    }
    return m;
  }, [ugmRows]);

  // Subscribed caps (active) vs current weekly burn → under-cover alert.
  const usdcCapRaw = status?.weeklyUsdcCap ? BigInt(status.weeklyUsdcCap) : 0n;
  const tkoCapRaw = status?.weeklyTakeoverCap ? BigInt(status.weeklyTakeoverCap) : 0n;
  const usdcCapUsd = Number(usdcCapRaw) / 1e6;
  const tkoCapUsd =
    takeoverPriceUsd > 0 ? parseFloat(formatUnits(tkoCapRaw, 18)) * takeoverPriceUsd : 0;
  const underCover =
    isActive &&
    ((budget.usdcSeatCount > 0 && usdcCapRaw < budget.weeklyUsdcRaw) ||
      (budget.takeoverSeatCount > 0 && tkoCapRaw < budget.weeklyTakeoverRaw));

  // Every top-up is pulled from the holder's OWN wallet at redeem time. If
  // the wallet can't cover the weekly cover cost for a token it holds tiles
  // in, those top-ups silently fail onchain (`RedemptionSkipped`) and the
  // tile can forfeit. Compare live wallet balances to the weekly burn and
  // warn. Soft nudge: a `null` balance (read failed / token unconfigured)
  // suppresses the warning so a transient RPC blip can't raise a false alarm.
  const { usdcRaw: walletUsdcRaw, takeoverRaw: walletTakeoverRaw } =
    useProtectionWalletFunding(address);
  const usdcUnderfunded =
    budget.usdcSeatCount > 0 &&
    budget.weeklyUsdcRaw > 0n &&
    walletUsdcRaw != null &&
    walletUsdcRaw < budget.weeklyUsdcRaw;
  const takeoverUnderfunded =
    budget.takeoverSeatCount > 0 &&
    budget.weeklyTakeoverRaw > 0n &&
    walletTakeoverRaw != null &&
    walletTakeoverRaw < budget.weeklyTakeoverRaw;
  const lowFundsTokens = [
    usdcUnderfunded ? 'USDC' : null,
    takeoverUnderfunded ? 'TAKEOVER' : null,
  ].filter((t): t is string => t !== null);

  const summaryLine = useMemo(() => {
    if (state === 'incompatible') {
      const wn = capability.result?.walletName;
      const named = wn && wn !== 'Unknown wallet' ? wn : 'This wallet';
      return `${named} isn’t supported yet — Land Protection works with MetaMask only right now.`;
    }
    if (isActive) {
      const parts: string[] = [];
      if (budget.usdcSeatCount > 0) parts.push(`$${usdcCapUsd.toFixed(2)} USDC`);
      if (budget.takeoverSeatCount > 0) parts.push(`$${tkoCapUsd.toFixed(2)} TAKEOVER`);
      return parts.length ? `Cover: ${parts.join(' + ')} / wk` : 'Protection active';
    }
    if (!budget.hasProtectableSeat) {
      // Tiles exist but every one is a V2.1 board MetaMask can't redeem
      // against → guide to a compatible wallet instead of leaving a
      // silently-disabled toggle.
      if (excludedV21Tiles > 0) {
        // Honest: a 7715 holder is a MetaMask Smart Account, so they
        // can't switch to Rabby/Coinbase/Frame for these older boards
        // (the address itself is bound). Don't suggest a wallet swap.
        return "MetaMask can't protect your older boards yet";
      }
      return 'No land to protect yet';
    }
    return `We'll cover ~$${budget.weeklyUsdEquivalent.toFixed(2)}/wk across your tiles`;
  }, [state, isActive, budget, usdcCapUsd, tkoCapUsd, excludedV21Tiles, capability]);

  const canEnable = budget.hasProtectableSeat;
  const pending = isEnabling || revokePending;
  const switchDisabled =
    state === 'loading' ||
    state === 'incompatible' ||
    pending ||
    (!isActive && !canEnable);

  const doEnable = async () => {
    setEnableError(null);
    try {
      await enable({
        weeklyUsdcCap: budget.usdcCap,
        weeklyTakeoverCap: budget.takeoverCap,
        runwayGateSeconds: RUNWAY_GATE_SECONDS,
        targetRunwaySeconds: TARGET_RUNWAY_SECONDS,
        // Path-filtered set (V2.1 dropped on the 7715 path). Without
        // this the server defaults to the full allowlist and 400s the
        // MetaMask grant after it's already been minted onchain.
        protectedUgms: protectableUgmsList,
      });
      // The mutation invalidates the status query; await an explicit
      // refetch so the toggle reads ACTIVE before the optimistic pending
      // state clears (otherwise the status-query race flashes it OFF).
      await refetchStatus();
    } catch (err) {
      handleTxError(err, 'ProtectionFooter.enable');
      // A deliberate cancel isn't a failure — leave the toggle OFF with
      // no alarm. Everything else surfaces inline so the user sees WHY
      // instead of a toggle that silently reverted.
      if (!isUserRejectionError(err)) {
        const raw = (err as Error)?.message ?? 'Could not enable Land Protection';
        setEnableError({
          message: toFriendlyAuthError(raw),
          needsSignIn: isWalletNotLinkedError(raw),
        });
      }
    }
  };

  const onToggle = (next: boolean) => {
    if (next) void doEnable();
    else setConfirmingOff(true);
  };

  // Turn OFF = revoke on-chain + clear the DB row. `revokeOnchain` sends
  // the on-chain disableDelegation tx on BOTH rails (a 7715 grant is an
  // MMDF delegation too), and POSTs /revoke so the server row is cleared.
  // A user-rejected wallet tx leaves protection ON.
  const onConfirmOff = async () => {
    setConfirmingOff(false);
    setOffNotice(null);
    try {
      await revokeOnchain();
      await refetchStatus();
    } catch (err) {
      handleTxError(err, 'ProtectionFooter.revoke');
      await refetchStatus();
      if (!isUserRejectionError(err)) {
        setOffNotice(
          toFriendlyAuthError((err as Error)?.message ?? 'Could not turn off Land Protection'),
        );
      }
    }
  };

  // Cost-led framing: the headline is the actual cost (≈1% of top-ups),
  // with the top-up ceiling as a secondary line — so the big number can
  // never read as a charge for the cover itself. The ceiling is the
  // signed cap (active) or the would-be cap (inactive); cost is 1% of it.
  // `showBigCover` hides the figures for the edge cases (incompatible
  // wallet, or nothing protectable), which fall back to `summaryLine`.
  const ceilingUsd = isActive
    ? usdcCapUsd + tkoCapUsd
    : Number(budget.usdcCap) / 1e6 +
      (takeoverPriceUsd > 0
        ? parseFloat(formatUnits(budget.takeoverCap, 18)) * takeoverPriceUsd
        : 0);
  const costUsd = ceilingUsd * 0.01;
  const coverSeats = budget.usdcSeatCount + budget.takeoverSeatCount;
  const showBigCover = state !== 'incompatible' && (isActive || canEnable);
  // `expiresAt` is stored in ms server-side but older rows / callers may
  // pass seconds — normalise so the countdown can't read as ~55 years.
  const expiresMs = status?.expiresAt
    ? status.expiresAt > 1e12
      ? status.expiresAt
      : status.expiresAt * 1000
    : 0;
  const daysLeft = expiresMs
    ? Math.max(0, Math.round((expiresMs - Date.now()) / 86_400_000))
    : null;

  return (
    <div className="shrink-0 w-full border-t border-amber-500/20 bg-[#0c0a06] p-4 flex flex-col gap-3 lg:w-[372px] lg:border-t-0 lg:border-l lg:h-full lg:overflow-y-auto">
      {/* Panel header */}
      <div className="flex items-center gap-2">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/land-protection.png"
          alt=""
          aria-hidden
          className="h-5 w-5 shrink-0 rounded-full object-cover"
        />
        <span className="text-[13px] font-semibold text-amber-100">Land Protection</span>
        <span className="ml-auto">
          <ProtectionStatusPill state={state} />
        </span>
      </div>

      {/* Weekly cover — a glanceable cost card (no longer a dialog trigger). */}
      {showBigCover ? (
        <div
          className={cn(
            'w-full text-left rounded-[6px] border px-3 py-2.5',
            isActive
              ? 'border-emerald-400/30 bg-emerald-500/[0.06]'
              : 'border-amber-400/25 bg-amber-500/[0.05]',
          )}
        >
          <div
            className={cn(
              'text-[9px] font-mono tracking-[0.2em] uppercase',
              isActive ? 'text-emerald-300/70' : 'text-amber-300/70',
            )}
          >
            Cost of cover
          </div>
          <div className="flex items-end gap-1.5">
            <span
              className={cn(
                'text-[22px] font-semibold leading-none',
                isActive ? 'text-emerald-200' : 'text-amber-100',
              )}
            >
              ≈ ${costUsd.toFixed(2)}
            </span>
            <span className="text-[11px] font-mono text-zinc-500 pb-0.5">/ wk</span>
          </div>
          <div className="mt-1 text-[11px] text-zinc-400">
            1% of top-ups · charged only when a tile is revived
          </div>
          <div className="mt-2 border-t border-white/[0.06] pt-1.5 text-[10px] font-mono text-zinc-500">
            {isActive ? 'Tops up to' : 'Will top up to'} ${ceilingUsd.toFixed(2)}/wk from your wallet
            {coverSeats > 0 ? ` · ${coverSeats} tile${coverSeats === 1 ? '' : 's'}` : ''}
          </div>
        </div>
      ) : (
        <div className="w-full text-left rounded-[6px] border border-white/[0.06] bg-black/30 px-3 py-2.5">
          {state === 'incompatible' && (
            <div className="mb-1 flex items-center gap-1.5">
              <MetaMaskMark className="h-4 w-4" />
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-amber-100">
                MetaMask only (for now)
              </span>
            </div>
          )}
          <div className="text-[11px] text-zinc-400">{summaryLine}</div>
        </div>
      )}

      {/* Toggle (or the incompatible-wallet "Why?" affordance) */}
      <div className="flex items-center gap-3 rounded-[4px] border border-white/[0.06] bg-black/40 px-3 py-2.5">
        <span className="flex-1 text-[11px] text-zinc-200">Auto top-up when runway runs low</span>
        {state === 'incompatible' ? (
          <button
            type="button"
            onClick={() => setHowOpen(true)}
            className="h-7 px-2 rounded-[3px] border border-amber-400/40 bg-amber-500/10 text-[10px] font-mono tracking-[0.16em] uppercase text-amber-200 hover:bg-amber-500/20 shrink-0"
          >
            Why?
          </button>
        ) : (
          // While a mutation is in flight, show the switch in its
          // optimistic target position (on while enabling, off while
          // pausing) + a pulse, so the pending state lives on the switch.
          <Switch
            checked={pending ? isEnabling : isActive}
            disabled={switchDisabled}
            onCheckedChange={onToggle}
            aria-busy={pending}
            aria-label="Land Protection"
            className={cn(
              'shrink-0 cursor-pointer transition-colors',
              'data-[state=unchecked]:bg-zinc-600 data-[state=unchecked]:hover:bg-zinc-400',
              'data-[state=checked]:bg-emerald-500 data-[state=checked]:hover:bg-emerald-400',
              pending && 'animate-pulse',
            )}
          />
        )}
      </div>

      {/* MetaMask-only note for the 'off' state only. The 'incompatible'
          state already leads with the fox + "MetaMask only (for now)"
          card, so gating on 'off' avoids double-stamping there; an active
          MetaMask holder never sees it. */}
      {state === 'off' && (
        <div className="flex items-center gap-1.5 text-[10px] font-mono tracking-[0.04em] text-zinc-500">
          <MetaMaskMark className="h-3 w-3" />
          <span>Currently MetaMask only (for now)</span>
        </div>
      )}

      {/* Protected boards + top-up history, tabbed. "Protected Boards"
          leads (it answers "what's actually covered?" — the gap that
          confused holders when the cover card sized from every tile but
          only allowlisted boards were really protected). The
          "Renews weekly…" line sits below the tabs, per the agreed
          layout. */}
      {isActive && (
        <>
          <ProtectionManageTabs
            coverageRows={coverageRows}
            historyItems={historyItems}
            boardByUgm={boardByUgm}
            address={address}
          />
          {daysLeft !== null && (
            <div className="text-[10px] font-mono tracking-[0.04em] text-zinc-500">
              Renews weekly · expires in {daysLeft} day{daysLeft === 1 ? '' : 's'}
            </div>
          )}
        </>
      )}

      {enableError && (
        <div
          role="alert"
          className={cn(
            'flex items-start gap-2 rounded-[4px] border px-2 py-1.5',
            enableError.needsSignIn
              ? 'border-amber-500/40 bg-amber-500/[0.08]'
              : 'border-rose-500/40 bg-rose-500/[0.08]',
          )}
        >
          <AlertTriangle
            className={cn(
              'h-3.5 w-3.5 shrink-0 mt-0.5',
              enableError.needsSignIn ? 'text-amber-300' : 'text-rose-300',
            )}
          />
          <span
            className={cn(
              'flex-1 text-[10px]',
              enableError.needsSignIn ? 'text-amber-100' : 'text-rose-100',
            )}
          >
            {enableError.message}
          </span>
          {enableError.needsSignIn ? (
            <button
              type="button"
              onClick={async () => {
                setEnableError(null);
                // Link/sign in the connected wallet, then retry the enable
                // it bounced on. A bare retry wouldn't help — the wallet
                // still wouldn't be on the session.
                try {
                  await wallet.signInActiveWallet();
                } catch {
                  /* user dismissed the Privy modal — leave the toggle OFF */
                }
                void doEnable();
              }}
              disabled={isEnabling}
              className="h-6 px-2 rounded-[3px] border border-amber-400 bg-amber-500/20 text-[10px] font-mono font-semibold tracking-[0.16em] uppercase text-amber-100 hover:bg-amber-500/30 disabled:opacity-60"
            >
              Sign In
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setEnableError(null);
                void doEnable();
              }}
              disabled={isEnabling}
              className="h-6 px-2 rounded-[3px] border border-rose-400 bg-rose-500/20 text-[10px] font-mono font-semibold tracking-[0.16em] uppercase text-rose-100 hover:bg-rose-500/30 disabled:opacity-60"
            >
              Try again
            </button>
          )}
        </div>
      )}

      {underCover && (
        <div className="flex items-center gap-2 rounded-[4px] border border-amber-500/40 bg-amber-500/[0.08] px-2 py-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-300 shrink-0" />
          <span className="flex-1 text-[10px] text-amber-100">
            Your weekly cover is below your current tax (~${budget.weeklyUsdEquivalent.toFixed(2)}/wk).
            Top it up to stay protected.
          </span>
          <button
            type="button"
            onClick={doEnable}
            disabled={isEnabling}
            className="h-6 px-2 rounded-[3px] border border-amber-400 bg-amber-500 text-black text-[10px] font-mono font-semibold tracking-[0.16em] uppercase hover:bg-amber-400 disabled:opacity-60"
          >
            Increase cover
          </button>
        </div>
      )}

      {/* Wallet too low to fund its own top-ups. Each redemption pulls
          principal + fee straight from this wallet, so an empty wallet means
          top-ups skip onchain and tiles can forfeit. Informational (no
          action button) — only the holder can add funds. */}
      {showBigCover && lowFundsTokens.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[4px] border border-amber-500/40 bg-amber-500/[0.08] px-2 py-1.5"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
          <span className="flex-1 text-[10px] leading-snug text-amber-100">
            Your wallet is low on {lowFundsTokens.join(' and ')}. Land Protection pays
            each top-up from this wallet, so your tiles may not be revived until you add
            more {lowFundsTokens.join(' / ')}.
          </span>
        </div>
      )}

      {confirmingOff && (
        <div className="flex items-center gap-2 rounded-[4px] border border-rose-500/30 bg-rose-500/[0.06] px-2 py-1.5">
          <span className="text-[10px] font-mono tracking-[0.16em] uppercase text-rose-100 flex-1">
            Turn off Land Protection? This revokes it on-chain &mdash; you&rsquo;ll re-sign to turn it back on.
          </span>
          <button
            type="button"
            onClick={() => setConfirmingOff(false)}
            className="h-6 px-2 rounded-[3px] border border-white/10 text-[10px] font-mono tracking-[0.16em] uppercase text-zinc-200 hover:text-white hover:border-white/30"
          >
            Keep on
          </button>
          <button
            type="button"
            onClick={onConfirmOff}
            className="h-6 px-2 rounded-[3px] border border-rose-400 bg-rose-500 text-white text-[10px] font-mono font-semibold tracking-[0.16em] uppercase hover:bg-rose-400"
          >
            Turn off
          </button>
        </div>
      )}

      {offNotice && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-[4px] border border-amber-500/40 bg-amber-500/[0.08] px-2 py-1.5"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
          <span className="flex-1 text-[10px] leading-snug text-amber-100">{offNotice}</span>
        </div>
      )}

      {/* Secondary actions — push to the bottom of the rail on desktop. */}
      <div className="mt-1 lg:mt-auto border-t border-white/[0.06] pt-2.5 flex flex-col gap-0.5">
        <button
          type="button"
          onClick={() => setHowOpen(true)}
          className="flex items-center gap-2 py-1 text-[11px] text-zinc-300 hover:text-amber-100 transition-colors"
        >
          <Info className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
          How it works
        </button>
      </div>

      <p className="text-[10px] leading-snug text-zinc-600">
        We only ever top up your own tiles, up to this weekly cap. Turn off anytime.
      </p>

      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent className="max-w-md bg-[#0b0a08] border-amber-500/30 text-zinc-100">
          <DialogHeader className="border-b border-amber-500/20 pb-3">
            <DialogTitle className="text-amber-100">How Land Protection works</DialogTitle>
            <DialogDescription className="text-zinc-400 pt-1">
              A safety net for tiles you can&rsquo;t check on every day.
            </DialogDescription>
          </DialogHeader>
          <ProtectionExplainer showMetaMaskNote={showMetaMaskNote} className="pt-1" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Tabbed manage section for the protection rail: "Protected Boards"
 * (default) then "Protection History". Each list scrolls independently
 * so a holder with many boards / top-ups never blows out the rail.
 *
 * Protected Boards lists EVERY board the holder owns tiles on, covered
 * or not. Un-covered boards carry a hover/tap tooltip with a brief,
 * non-technical reason (see `board-coverage.ts`) — that turns the silent
 * "why is only 1 of my boards covered?" gap into something actionable.
 */
function ProtectionManageTabs({
  coverageRows,
  historyItems,
  boardByUgm,
  address,
}: {
  coverageRows: BoardCoverageRow[];
  historyItems: ProtectionHistoryItem[];
  boardByUgm: Map<string, { label: string; hue: string }>;
  address: Address | null;
}) {
  return (
    <Tabs defaultValue="boards" className="w-full">
      <TabsList className="grid h-8 w-full grid-cols-2 rounded-[4px] border border-white/[0.06] bg-black/30 p-0.5">
        <TabsTrigger
          value="boards"
          className="h-full rounded-[3px] text-[10px] font-mono uppercase tracking-[0.08em] text-zinc-400 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-100 data-[state=active]:shadow-none"
        >
          Protected Boards
        </TabsTrigger>
        <TabsTrigger
          value="history"
          className="h-full rounded-[3px] text-[10px] font-mono uppercase tracking-[0.08em] text-zinc-400 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-100 data-[state=active]:shadow-none"
        >
          Protection History
        </TabsTrigger>
      </TabsList>

      {/* ── Protected boards (covered + not-covered, with reason) ── */}
      <TabsContent value="boards" className="mt-2">
        {coverageRows.length === 0 ? (
          <p className="rounded-[4px] border border-white/[0.06] bg-black/30 px-2.5 py-3 text-center text-[10px] text-zinc-500">
            No boards to show yet.
          </p>
        ) : (
          <ul className="max-h-[180px] divide-y divide-white/[0.04] overflow-y-auto rounded-[4px] border border-white/[0.06] bg-black/30">
            {coverageRows.map((row) => {
              const seatCount = row.board.seats.length;
              return (
                <li
                  key={`${row.ugmKey}-${row.board.poolId}`}
                  className="flex items-center gap-2 px-2.5 py-2"
                >
                  <BoardCover
                    seed={row.board.poolId}
                    hue={historyBoardHue(row.board.poolId)}
                    initials={boardInitials(row.board.poolName)}
                    imageUrl={row.board.poolImageUrl ?? null}
                    size={20}
                    className="shrink-0"
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate font-mono text-[11px]',
                      row.covered ? 'text-zinc-200' : 'text-zinc-400',
                    )}
                  >
                    {row.board.poolName}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-zinc-500">
                    {seatCount} tile{seatCount === 1 ? '' : 's'}
                  </span>
                  {row.covered ? (
                    <CheckCircle2
                      className="h-3.5 w-3.5 shrink-0 text-emerald-300"
                      aria-label="Covered"
                    />
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Why this board is not covered"
                          className="shrink-0 inline-flex items-center gap-1 rounded-[3px] px-1 py-0.5 text-[10px] text-amber-300/90 hover:text-amber-200"
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Why?
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-xs leading-snug">
                        {row.reason ? boardCoverageReasonCopy(row.reason) : null}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </TabsContent>

      {/* ── Top-up history ── */}
      <TabsContent value="history" className="mt-2">
        {historyItems.length === 0 ? (
          <p className="rounded-[4px] border border-white/[0.06] bg-black/30 px-2.5 py-3 text-center text-[10px] text-zinc-500">
            No top-ups yet &mdash; they&rsquo;ll appear here when a tile is revived.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <ul className="max-h-[180px] divide-y divide-white/[0.04] overflow-y-auto rounded-[4px] border border-white/[0.06] bg-black/30">
              {historyItems.map((item) => {
                const meta = boardByUgm.get(item.ugm.toLowerCase());
                return (
                  <li key={item.id} className="flex items-center gap-2 px-2.5 py-2">
                    <span
                      aria-hidden
                      className="h-3 w-3 shrink-0 rounded-[3px]"
                      style={{ backgroundColor: meta?.hue ?? '#52525b' }}
                    />
                    <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-zinc-200">
                      {meta?.label ?? `${item.ugm.slice(0, 6)}…${item.ugm.slice(-4)}`}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-emerald-300">
                      {formatHistoryAmount(item)}
                    </span>
                    {item.executedAt && (
                      <span className="shrink-0 font-mono text-[10px] text-zinc-500">
                        {timeAgoShort(item.executedAt)}
                      </span>
                    )}
                    {item.executedTx && (
                      <a
                        href={`https://basescan.org/tx/${item.executedTx}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-zinc-500 transition-colors hover:text-zinc-200"
                        aria-label="View transaction on basescan"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
            <a
              href={`https://basescan.org/address/${address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 self-end font-mono text-[10px] text-zinc-500 transition-colors hover:text-amber-100"
            >
              Show all
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
